import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiFileText, FiDownload, FiRefreshCw } from 'react-icons/fi';
import tributarioService from '../services/tributarioService';
import Loading from '../components/Loading';
import './LibroCompraVentaPage.css';

const LibroCompraVentaPage = () => {
  const [libro, setLibro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [tipoLibro, setTipoLibro] = useState('compra');

  const cargarLibro = async () => {
    try {
      setLoading(true);
      const res = await tributarioService.libros.getAll({ periodo_mes: mes, periodo_anio: anio, tipo: tipoLibro });
      const libros = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      if (libros.length > 0) {
        const libroDetalle = await tributarioService.libros.getById(libros[0].id);
        setLibro(libroDetalle.data);
      } else {
        setLibro(null);
      }
    } catch { toast.error('Error al cargar libro'); }
    finally { setLoading(false); }
  };

  const generarLibro = async () => {
    try {
      setLoading(true);
      const res = await tributarioService.libros.create({ periodo_mes: mes, periodo_anio: anio, tipo: tipoLibro });
      if (res.data?.id) {
        await tributarioService.libros.generar(res.data.id);
      }
      toast.success('Libro generado exitosamente');
      cargarLibro();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al generar libro'); setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const exportarExcel = async () => {
    try {
      const XLSX = (await import('xlsx')).default;
      const detalles = libro?.detalles || [];
      const ws = XLSX.utils.json_to_sheet(detalles.map(d => ({
        'N° Documento': d.numero_documento,
        'RUT': d.rut,
        'Razon Social': d.razon_social,
        'Neto': d.monto_neto,
        'IVA': d.monto_iva,
        'Exento': d.monto_exento,
        'Total': d.monto_total,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `Libro ${tipoLibro}`);
      XLSX.writeFile(wb, `libro_${tipoLibro}_${anio}_${mes}.xlsx`);
      toast.success('Excel exportado');
    } catch { toast.error('Error al exportar'); }
  };

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const detalles = libro?.detalles || libro?.results || [];

  return (
    <div className="libro-cv-page">
      <div className="page-header">
        <h1>Libro de Compras y Ventas</h1>
      </div>

      <div className="selector-bar">
        <div className="form-group">
          <label>Tipo</label>
          <select value={tipoLibro} onChange={e => setTipoLibro(e.target.value)}>
            <option value="compra">Libro de Compras</option>
            <option value="venta">Libro de Ventas</option>
          </select>
        </div>
        <div className="form-group">
          <label>Mes</label>
          <select value={mes} onChange={e => setMes(parseInt(e.target.value))}>
            {meses.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Ano</label>
          <select value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
            {[2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <button className="btn-primary" onClick={cargarLibro}><FiRefreshCw /> Consultar</button>
        <button className="btn-secondary" onClick={generarLibro}><FiFileText /> Generar Libro</button>
      </div>

      {loading && <Loading />}

      {!loading && libro && (
        <>
          {/* Totales */}
          <div className="totales-bar">
            <div className="total-item"><span>Total Neto</span><strong>{formatCurrency(libro.total_neto)}</strong></div>
            <div className="total-item"><span>Total IVA</span><strong>{formatCurrency(libro.total_iva)}</strong></div>
            <div className="total-item"><span>Total Exento</span><strong>{formatCurrency(libro.total_exento)}</strong></div>
            <div className="total-item total-highlight"><span>Total General</span><strong>{formatCurrency(libro.total_neto + libro.total_iva + libro.total_exento)}</strong></div>
            <button className="btn-export" onClick={exportarExcel}><FiDownload /> Excel</button>
          </div>

          {/* Tabla */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>N° Doc</th>
                  <th>Tipo Doc</th>
                  <th>Fecha</th>
                  <th>RUT</th>
                  <th>Razon Social</th>
                  <th style={{ textAlign: 'right' }}>Neto</th>
                  <th style={{ textAlign: 'right' }}>IVA</th>
                  <th style={{ textAlign: 'right' }}>Exento</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {detalles.length === 0 ? (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>No hay registros para este periodo</td></tr>
                ) : detalles.map((d, i) => (
                  <tr key={i}>
                    <td className="mono">{d.numero_documento}</td>
                    <td>{d.tipo_documento_display || d.tipo_documento}</td>
                    <td>{d.fecha}</td>
                    <td className="mono">{d.rut}</td>
                    <td>{d.razon_social}</td>
                    <td className="mono right">{formatCurrency(d.monto_neto)}</td>
                    <td className="mono right">{formatCurrency(d.monto_iva)}</td>
                    <td className="mono right">{formatCurrency(d.monto_exento)}</td>
                    <td className="mono right bold">{formatCurrency(d.monto_total)}</td>
                  </tr>
                ))}
              </tbody>
              {detalles.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'right', fontWeight: 700 }}>TOTALES</td>
                    <td className="mono right bold">{formatCurrency(detalles.reduce((s, d) => s + parseFloat(d.monto_neto || 0), 0))}</td>
                    <td className="mono right bold">{formatCurrency(detalles.reduce((s, d) => s + parseFloat(d.monto_iva || 0), 0))}</td>
                    <td className="mono right bold">{formatCurrency(detalles.reduce((s, d) => s + parseFloat(d.monto_exento || 0), 0))}</td>
                    <td className="mono right bold">{formatCurrency(detalles.reduce((s, d) => s + parseFloat(d.monto_total || 0), 0))}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default LibroCompraVentaPage;
